/**
 * GoldN UTM preserve + cart-attribute backup (Kaching subscription fix 2026-09-17)
 *
 * WHY:
 * - Existing link rewriter kept UTMs on same-origin PDP navigations (#1104 Bundles GREEN).
 * - It intentionally skipped /checkout and never wrote cart attributes.
 * - Kaching Subscriptions ATC uses Shopify /cart/add.js + selling_plan then
 *   window.location = /checkout. Native landing_site depends on Shopify visit cookies.
 * - #1105 (Kaching Subscription First Order) had landing_site=null, referring_site=null,
 *   customerJourney momentsCount=0, note_attributes=[], UA DuckDuckGo — visit cookies
 *   never bound to the order. Link rewrite alone cannot recover that.
 *
 * FIX:
 * 1) Keep sessionStorage capture + same-origin href decoration (unchanged).
 * 2) Persist captured params to cart attributes via POST /cart/update.js so they
 *    survive into order.note_attributes / customAttributes even when landing_site is blank.
 * 3) Re-sync after cart/add (Kaching fetch) and before checkout navigation.
 *
 * SCOPE: measurement / attribution only. Not Meta pixel / ATC / Purchase unlock.
 */
(function (root) {
  var STORE_KEY = 'goldn_attr_params';
  var COOKIE_KEY = 'goldn_attr_params';
  var ATTR_KEY_RE = /^(utm_[a-z0-9_]+|gclid|gbraid|wbraid|fbclid|srsltid|msclkid|ttclid)$/i;
  var CART_ATTR_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'utm_id',
    'gclid',
    'fbclid'
  ];
  var syncTimer = null;
  var lastSyncedSig = '';
  var origReplaceState =
    root.history && root.history.replaceState
      ? root.history.replaceState.bind(root.history)
      : null;
  var origPushState =
    root.history && root.history.pushState
      ? root.history.pushState.bind(root.history)
      : null;

  function readFromSearch(search) {
    var out = {};
    var params;
    try {
      params = new URLSearchParams(search || '');
    } catch (e) {
      return out;
    }
    params.forEach(function (value, key) {
      if (ATTR_KEY_RE.test(key) && value) out[key] = value;
    });
    return out;
  }

  function mergeParams(base, extra) {
    var next = {};
    Object.keys(base || {}).forEach(function (key) {
      next[key] = base[key];
    });
    Object.keys(extra || {}).forEach(function (key) {
      if (extra[key] && !next[key]) next[key] = extra[key];
    });
    return next;
  }

  function loadCookie() {
    try {
      var match = (root.document && root.document.cookie || '').match(
        new RegExp('(?:^|; )' + COOKIE_KEY + '=([^;]*)')
      );
      if (!match) return {};
      return JSON.parse(decodeURIComponent(match[1])) || {};
    } catch (e) {
      return {};
    }
  }

  function saveCookie(params) {
    try {
      if (!root.document) return;
      var merged = mergeParams(loadCookie(), params);
      if (!Object.keys(merged).length) return;
      var maxAge = 60 * 60 * 24 * 30; // 30d
      root.document.cookie =
        COOKIE_KEY +
        '=' +
        encodeURIComponent(JSON.stringify(merged)) +
        '; path=/; max-age=' +
        maxAge +
        '; SameSite=Lax';
    } catch (e) {}
  }

  function loadStore() {
    var fromSession = {};
    if (root.sessionStorage) {
      try {
        var raw = root.sessionStorage.getItem(STORE_KEY);
        fromSession = raw ? JSON.parse(raw) : {};
      } catch (e) {
        fromSession = {};
      }
    }
    return mergeParams(fromSession, loadCookie());
  }

  function saveStore(params) {
    var merged = mergeParams(loadStore(), params);
    if (!Object.keys(merged).length) return;
    if (root.sessionStorage) {
      try {
        root.sessionStorage.setItem(STORE_KEY, JSON.stringify(merged));
      } catch (e) {}
    }
    saveCookie(merged);
  }

  function isSkippableHref(href) {
    if (!href) return true;
    var trimmed = String(href).trim();
    if (!trimmed || trimmed.charAt(0) === '#') return true;
    if (/^(mailto:|tel:|javascript:|sms:)/i.test(trimmed)) return true;
    return false;
  }

  function withAttr(href, stored, origin) {
    if (isSkippableHref(href)) return href;
    var keys = stored || {};
    if (!Object.keys(keys).length) return href;
    var url;
    try {
      url = new URL(
        href,
        origin || (root.location && root.location.origin) || 'https://areyougoldn.com'
      );
    } catch (e) {
      return href;
    }
    var here = origin || (root.location && root.location.origin);
    // Do not decorate cross-origin or /checkout — cart attributes are the checkout path.
    if (here && url.origin !== here) return href;
    if (/\/checkout(\/|$)/i.test(url.pathname)) return href;
    var changed = false;
    Object.keys(keys).forEach(function (key) {
      if (!url.searchParams.has(key) && keys[key]) {
        url.searchParams.set(key, keys[key]);
        changed = true;
      }
    });
    if (!changed) return href;
    return url.pathname + url.search + url.hash;
  }

  function decorateAnchor(anchor, stored) {
    if (!anchor || !anchor.getAttribute) return;
    var href = anchor.getAttribute('href');
    if (isSkippableHref(href)) return;
    var next = withAttr(href, stored, root.location && root.location.origin);
    if (next !== href) anchor.setAttribute('href', next);
  }

  function decorateLinks(scope, stored) {
    var params = stored || loadStore();
    if (!Object.keys(params).length) return;
    var rootNode = scope && scope.querySelectorAll ? scope : root.document;
    if (!rootNode) return;
    rootNode.querySelectorAll('a[href]').forEach(function (anchor) {
      decorateAnchor(anchor, params);
    });
  }

  function restoreCurrentUrl(stored) {
    if (!origReplaceState || !root.location) return;
    var params = stored || loadStore();
    if (!Object.keys(params).length) return;
    var current = root.location.pathname + root.location.search + root.location.hash;
    var next = withAttr(current, params, root.location.origin);
    if (next !== current) origReplaceState(root.history.state, '', next);
  }

  function wrapHistory(method, original) {
    if (!original) return;
    root.history[method] = function (state, title, url) {
      if (url) url = withAttr(String(url), loadStore(), root.location && root.location.origin);
      return original(state, title, url);
    };
  }

  function captureLanding() {
    if (!root.location) return loadStore();
    var fromUrl = readFromSearch(root.location.search);
    saveStore(fromUrl);
    return mergeParams(loadStore(), fromUrl);
  }

  function cartAttributesPayload(params) {
    var attrs = {};
    var has = false;
    CART_ATTR_KEYS.forEach(function (key) {
      if (params[key]) {
        attrs[key] = String(params[key]).slice(0, 255);
        has = true;
      }
    });
    // Also store a compact landing hint for ops/debug (not used for FR money rule).
    if (params.utm_campaign || params.utm_source) {
      try {
        attrs.goldn_landing =
          (root.location && root.location.pathname ? root.location.pathname : '') +
          (params.utm_campaign ? '?utm_campaign=' + params.utm_campaign : '');
        attrs.goldn_landing = String(attrs.goldn_landing).slice(0, 255);
        has = true;
      } catch (e) {}
    }
    return has ? attrs : null;
  }

  function signature(attrs) {
    try {
      return JSON.stringify(attrs);
    } catch (e) {
      return '';
    }
  }

  function syncCartAttributes(force) {
    var params = loadStore();
    var attrs = cartAttributesPayload(params);
    if (!attrs) return Promise.resolve(false);
    var sig = signature(attrs);
    if (!force && sig === lastSyncedSig) return Promise.resolve(false);
    lastSyncedSig = sig;
    // v10 2026-11-02: use the unhooked fetch so our own update.js does not re-trigger a sync (was an endless 50 ms loop).
    var send = root.__goldnUtmOrigFetch || root.fetch.bind(root);
    return send('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify({ attributes: attrs })
      })
      .then(function (res) {
        return res.ok;
      })
      .catch(function () {
        // Allow one retry on next tick if cart wasn't ready.
        lastSyncedSig = '';
        return false;
      });
  }

  function scheduleSync(force) {
    if (syncTimer) root.clearTimeout(syncTimer);
    syncTimer = root.setTimeout(function () {
      syncCartAttributes(!!force);
    }, 50);
  }

  function observeLinks() {
    if (!root.document || !root.document.documentElement || !root.MutationObserver) return;
    var obs = new MutationObserver(function (mutations) {
      var params = loadStore();
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (!node || node.nodeType !== 1) return;
          if (node.matches && node.matches('a[href]')) decorateAnchor(node, params);
          else decorateLinks(node, params);
        });
      });
    });
    obs.observe(root.document.documentElement, { childList: true, subtree: true });
  }

  function hookFetch() {
    if (!root.fetch || root.__goldnUtmFetchHooked) return;
    root.__goldnUtmFetchHooked = true;
    var origFetch = root.fetch.bind(root);
    root.__goldnUtmOrigFetch = origFetch;
    root.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : input && input.url ? input.url : '';
      var p = origFetch(input, init);
      try {
        if (/\/cart\/(add|change|clear)(\.js)?(\?|$)/i.test(String(url))) {
          p.then(function () {
            scheduleSync(true);
          }).catch(function () {});
        }
      } catch (e) {}
      return p;
    };
  }

  function hookCheckoutNav() {
    if (!root.document || root.__goldnUtmCheckoutHooked) return;
    root.__goldnUtmCheckoutHooked = true;
    root.document.addEventListener(
      'click',
      function (ev) {
        var t = ev.target;
        if (!t || !t.closest) return;
        var a = t.closest('a[href]');
        if (!a) return;
        var href = a.getAttribute('href') || '';
        if (!/\/checkout(\/|$)/i.test(href) && href !== '/checkout') return;
        // Best-effort flush before Kaching/theme sends user to checkout.
        try {
          syncCartAttributes(true);
        } catch (e) {}
      },
      true
    );
  }

  function boot() {
    var stored = captureLanding();
    restoreCurrentUrl(stored);
    decorateLinks(root.document, stored);
    observeLinks();
    hookFetch();
    hookCheckoutNav();
    scheduleSync(false);
  }

  var api = {
    ATTR_KEY_RE: ATTR_KEY_RE,
    readFromSearch: readFromSearch,
    mergeParams: mergeParams,
    withAttr: withAttr,
    isSkippableHref: isSkippableHref,
    syncCartAttributes: syncCartAttributes,
    loadStore: loadStore
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
  root.GoldnUtmPreserve = api;

  if (root.document) {
    wrapHistory('replaceState', origReplaceState);
    wrapHistory('pushState', origPushState);
    if (root.document.readyState === 'loading') {
      captureLanding();
      restoreCurrentUrl(loadStore());
      root.document.addEventListener('DOMContentLoaded', boot, { once: true });
    } else {
      boot();
    }
  }
})(typeof window !== 'undefined' ? window : globalThis);
